import {type ReactNode, useCallback, useEffect, useState} from "react";
import {Alert, Box, Button, InputAdornment, Paper, Stack, TextField, Typography} from "@mui/material";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import {alpha} from "@mui/material/styles";
import {useTranslation} from "react-i18next";
import {BillingConfig, BillingInfo, type LegalEntity} from "../AdminTypes";
import BillingDeleteOverrideDialog from "./BillingDeleteOverrideDialog";
import {formatPriceDraft, parsePriceDraft} from "./billingUtils";
import {
    useDeleteLegalEntityBillingPrice,
    useUpdateDefaultBillingPrice,
    useUpdateLegalEntityBillingPrice,
} from "../../../api/admin/adminQueries";

const SettingsPanel = ({
    eyebrow,
    title,
    description,
    children,
}: {
    eyebrow: string;
    title: string;
    description: string;
    children: ReactNode;
}) => (
    <Paper
        elevation={0}
        sx={(theme) => ({
            p: {xs: 2.25, md: 2.5},
            borderRadius: 3,
            border: "1px solid",
            borderColor: theme.palette.divider,
            bgcolor: alpha(theme.palette.background.paper, theme.palette.mode === "light" ? 0.86 : 0.94),
        })}
    >
        <Stack spacing={2.25}>
            <Box>
                <Typography
                    sx={(theme) => ({
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "uppercase",
                        letterSpacing: 0.8,
                        color: theme.palette.text.secondary,
                        mb: 0.5,
                    })}
                >
                    {eyebrow}
                </Typography>
                <Typography
                    sx={(theme) => ({
                        fontFamily: theme.typography.h4.fontFamily || theme.typography.fontFamily,
                        fontWeight: 800,
                        fontSize: 21,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                    })}
                >
                    {title}
                </Typography>
                <Typography color="text.secondary">{description}</Typography>
            </Box>
            {children}
        </Stack>
    </Paper>
);

type BillingPriceSettingsProps = {
    billingInfo: BillingInfo | undefined;
    billingConfig: BillingConfig | undefined;
    billingConfigIsLoading: boolean;
    selectedLegalEntity: LegalEntity | null;
};

const BillingPriceSettings = ({
    billingInfo,
    billingConfig,
    billingConfigIsLoading,
    selectedLegalEntity,
}: BillingPriceSettingsProps) => {
    const {t: translate} = useTranslation();

    const [defaultPriceDraft, setDefaultPriceDraft] = useState("");
    const [legalEntityPriceDraft, setLegalEntityPriceDraft] = useState("");
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);

    const parsedDefaultPriceDraft = parsePriceDraft(defaultPriceDraft);
    const parsedLegalEntityPriceDraft = parsePriceDraft(legalEntityPriceDraft);

    const updateDefaultBillingPriceMutation = useUpdateDefaultBillingPrice();
    const updateLegalEntityBillingPriceMutation = useUpdateLegalEntityBillingPrice();
    const deleteLegalEntityBillingPriceMutation = useDeleteLegalEntityBillingPrice();

    const hasSelectedLegalEntity: boolean = selectedLegalEntity !== null;

    const currentDefaultPrice = billingConfig?.defaultPricePerToken;
    const legalEntityPricePerToken = billingInfo?.pricePerToken;
    const hasLegalEntityOverride = legalEntityPricePerToken !== null && legalEntityPricePerToken !== undefined;
    const currentEffectiveLegalEntityPrice = legalEntityPricePerToken ?? currentDefaultPrice ?? null;

    const defaultPriceErrorMessage = updateDefaultBillingPriceMutation.error?.message || "";
    const legalEntityPriceErrorMessage = updateLegalEntityBillingPriceMutation.error?.message || "";
    const isUpdatingBillingPrice =
        updateDefaultBillingPriceMutation.isPending ||
        updateLegalEntityBillingPriceMutation.isPending ||
        deleteLegalEntityBillingPriceMutation.isPending;
    const isDeletePending = deleteLegalEntityBillingPriceMutation.isPending;

    const isDefaultPriceChanged =
        parsedDefaultPriceDraft !== null &&
        currentDefaultPrice !== undefined &&
        parsedDefaultPriceDraft !== currentDefaultPrice;
    const isLegalEntityPriceChanged =
        parsedLegalEntityPriceDraft !== null &&
        currentEffectiveLegalEntityPrice !== null &&
        parsedLegalEntityPriceDraft !== currentEffectiveLegalEntityPrice;

    useEffect(() => {
        setDefaultPriceDraft(formatPriceDraft(currentDefaultPrice));
    }, [currentDefaultPrice]);

    useEffect(() => {
        setLegalEntityPriceDraft(formatPriceDraft(currentEffectiveLegalEntityPrice));
    }, [currentEffectiveLegalEntityPrice, selectedLegalEntity?.id]);

    const handleUpdateDefaultPrice = async () => {
        if (!isDefaultPriceChanged) {
            return;
        }

        await updateDefaultBillingPriceMutation.mutateAsync({
            pricePerToken: defaultPriceDraft,
        });
    };

    const handleUpdateLegalEntityPrice = async () => {
        if (!selectedLegalEntity || !isLegalEntityPriceChanged) {
            return;
        }

        await updateLegalEntityBillingPriceMutation.mutateAsync({
            legalEntityId: selectedLegalEntity.id,
            pricePerToken: legalEntityPriceDraft,
        });
    };

    const handleOpenDeleteLegalEntityPrice = useCallback(() => {
        if (!hasLegalEntityOverride) {
            return;
        }
        setIsDeleteDialogOpen(true);
        deleteLegalEntityBillingPriceMutation.reset();
    }, [deleteLegalEntityBillingPriceMutation, hasLegalEntityOverride]);

    const handleCloseDeleteLegalEntityPrice = useCallback(() => {
        deleteLegalEntityBillingPriceMutation.reset();
    }, [deleteLegalEntityBillingPriceMutation]);

    const handleDeleteLegalEntityPrice = async () => {
        if (!isDeleteDialogOpen || selectedLegalEntity == null) {
            return;
        }
        await deleteLegalEntityBillingPriceMutation.mutateAsync({
            legalEntityId: selectedLegalEntity.id,
        });
        setIsDeleteDialogOpen(false);
        setLegalEntityPriceDraft(formatPriceDraft(currentDefaultPrice));
    };

    return (
        <Stack direction={{xs: "column", lg: "row"}} spacing={2} alignItems="stretch">
            <Box sx={{flex: 1, minWidth: 0}}>
                <SettingsPanel
                    eyebrow={translate("admin.billing.defaultPrice.eyebrow")}
                    title={translate("admin.billing.defaultPrice.title")}
                    description={translate("admin.billing.defaultPrice.description")}
                >
                    {defaultPriceErrorMessage ? <Alert severity="error">{defaultPriceErrorMessage}</Alert> : null}
                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: {xs: "1fr", md: "minmax(0, 320px) auto"},
                            columnGap: 1.5,
                            rowGap: 0.75,
                            alignItems: "center",
                        }}
                    >
                        <TextField
                            label={translate("admin.billing.defaultPrice.label")}
                            value={defaultPriceDraft}
                            onChange={(event) => setDefaultPriceDraft(event.target.value)}
                            disabled={billingConfigIsLoading || isUpdatingBillingPrice}
                            sx={{
                                width: {xs: "100%", md: 320},
                                gridColumn: 1,
                                gridRow: 1,
                            }}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">EUR</InputAdornment>,
                            }}
                            inputProps={{
                                inputMode: "decimal",
                            }}
                        />
                        <Button
                            variant="contained"
                            startIcon={<SaveRoundedIcon />}
                            onClick={() => void handleUpdateDefaultPrice()}
                            disabled={isUpdatingBillingPrice || !isDefaultPriceChanged}
                            sx={{
                                fontWeight: 700,
                                boxShadow: "none",
                                minWidth: 170,
                                width: "50%",
                                alignSelf: {xs: "stretch", md: "center"},
                                gridColumn: {xs: 1, md: 2},
                                gridRow: {xs: 3, md: 1},
                            }}
                        >
                            {translate("admin.billing.defaultPrice.save")}
                        </Button>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                gridColumn: 1,
                                gridRow: {xs: 2, md: 2},
                                px: 1.75,
                            }}
                        >
                            {translate("admin.billing.defaultPrice.helperText")}
                        </Typography>
                    </Box>
                </SettingsPanel>
            </Box>

            <Box sx={{flex: 1, minWidth: 0}}>
                <SettingsPanel
                    eyebrow={translate("admin.billing.legalEntityPrice.eyebrow")}
                    title={translate("admin.billing.legalEntityPrice.title")}
                    description={translate("admin.billing.legalEntityPrice.description")}
                >
                    {legalEntityPriceErrorMessage ? (
                        <Alert severity="error">{legalEntityPriceErrorMessage}</Alert>
                    ) : null}
                    {!hasSelectedLegalEntity ? (
                        <Alert severity="info">{translate("admin.billing.legalEntityPrice.selectLegalEntity")}</Alert>
                    ) : (
                        <Stack spacing={2}>
                            <TextField
                                label={translate("admin.shared.legalEntity")}
                                value={
                                    selectedLegalEntity?.name ?? translate("admin.billing.selectedLegalEntityFallback")
                                }
                                fullWidth
                                InputProps={{readOnly: true}}
                            />
                            <Box
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: {
                                        xs: "1fr",
                                        md: "minmax(0, 320px) auto auto",
                                    },
                                    columnGap: 1.5,
                                    rowGap: 0.75,
                                    alignItems: "center",
                                }}
                            >
                                <TextField
                                    label={translate("admin.billing.legalEntityPrice.label")}
                                    value={legalEntityPriceDraft}
                                    onChange={(event) => setLegalEntityPriceDraft(event.target.value)}
                                    disabled={isUpdatingBillingPrice}
                                    sx={{
                                        width: {xs: "100%", md: 320},
                                        gridColumn: 1,
                                        gridRow: 1,
                                    }}
                                    InputProps={{
                                        startAdornment: <InputAdornment position="start">EUR</InputAdornment>,
                                    }}
                                    inputProps={{
                                        inputMode: "decimal",
                                    }}
                                />
                                <Button
                                    variant="contained"
                                    startIcon={<SaveRoundedIcon />}
                                    onClick={() => void handleUpdateLegalEntityPrice()}
                                    disabled={isUpdatingBillingPrice || !isLegalEntityPriceChanged}
                                    sx={{
                                        fontWeight: 700,
                                        boxShadow: "none",
                                        minWidth: 170,
                                        alignSelf: {xs: "stretch", md: "center"},
                                        gridColumn: {xs: 1, md: 2},
                                        gridRow: {xs: 3, md: 1},
                                    }}
                                >
                                    {translate("admin.billing.legalEntityPrice.save")}
                                </Button>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    startIcon={<DeleteOutlineRoundedIcon />}
                                    onClick={handleOpenDeleteLegalEntityPrice}
                                    disabled={isUpdatingBillingPrice || !hasLegalEntityOverride}
                                    sx={{
                                        fontWeight: 700,
                                        minWidth: 160,
                                        alignSelf: {xs: "stretch", md: "center"},
                                        gridColumn: {xs: 1, md: 3},
                                        gridRow: {xs: 4, md: 1},
                                    }}
                                >
                                    {translate("admin.billing.legalEntityPrice.delete")}
                                </Button>
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{
                                        gridColumn: 1,
                                        gridRow: {xs: 2, md: 2},
                                        px: 1.75,
                                    }}
                                >
                                    {hasLegalEntityOverride
                                        ? translate("admin.billing.legalEntityPrice.overrideHelper")
                                        : translate("admin.billing.legalEntityPrice.inheritsDefault")}
                                </Typography>
                            </Box>
                        </Stack>
                    )}
                </SettingsPanel>
            </Box>
            <BillingDeleteOverrideDialog
                deleteErrorMessage={legalEntityPriceErrorMessage}
                isDeletePending={isDeletePending}
                isDeleteDialogOpen={isDeleteDialogOpen}
                selectedEntity={selectedLegalEntity}
                onClose={handleCloseDeleteLegalEntityPrice}
                onConfirm={handleDeleteLegalEntityPrice}
            />
        </Stack>
    );
};

export default BillingPriceSettings;
